import type { ClassValue } from 'clsx'
import type { Locale } from '~/i18n/i18n.config'
import axios from 'axios'
import { clsx } from 'clsx'

import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function getDateTimeParts(date: string, lang: Locale) {
  const dateParsed = new Date(date)

  if (Number.isNaN(dateParsed.getTime())) {
    return null
  }

  const formatter = new Intl.DateTimeFormat(lang === 'es' ? 'es-ES' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/Madrid',
  })

  const dateParts = formatter.formatToParts(dateParsed)
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    dateParts.find(part => part.type === type)?.value ?? ''
  const month = getPart('month')
  const day = getPart('day')
  const year = getPart('year')
  const hour = getPart('hour')
  const minute = getPart('minute')

  return {
    day,
    month,
    year,
    hour,
    minute,
  }
}

export function decodeJWT(token: string): {
  exp: number
  iat: number
  sub: string
  type: 'access' | 'refresh'
} {
  const payload = token.split('.')[1] || ''
  return JSON.parse(Buffer.from(payload, 'base64').toString('utf8'))
}

export async function saveFile(url?: string, filename?: string) {
  if (!url || !filename) {
    throw new Error('URL and filename are required')
  }
  try {
    // Create a temporary link element
    const link = document.createElement('a')
    let blob: Blob

    if (url.includes('https://')) {
      const response = await axios.get(url, {
        responseType: 'blob',
      })
      blob = new Blob([response.data])
    }
    else {
      throw new Error('File is not downloadable')
    }

    link.href = window.URL.createObjectURL(blob)
    link.download = filename

    // Trigger the download
    link.click()

    // Clean up the temporary link element
    window.URL.revokeObjectURL(link.href)
    link.remove()
  }
  catch (error) {
    throw new Error('Error downloading file:', error as ErrorOptions)
  }
}
